import { PagePath } from 'constants/pagePath';
import { useCallback, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Client } from 'urql';
import { LogoutDocument } from 'views/utils/graphql/graphql';
import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

type AuthStore = {
  isLogin: boolean;
  token: string;
  loggedInPath: string | null;
  logoutPath: string | null;
  getToken: () => string;
  saveToken: (token: string) => void;
  logout: (currentPath: string) => void;
  forceLogout: (currentPath: string) => void;
};

export const useAuthStore = create(
  persist<AuthStore>(
    (set, get) => ({
      isLogin: false,
      token: '',
      loggedInPath: null,
      logoutPath: null,
      getToken: () => get().token,
      saveToken: (token: string) => set(() => ({ isLogin: true, token, loggedInPath: null, logoutPath: '' })),
      logout: (currentPath: string) =>
        set(() => ({
          isLogin: false,
          token: '',
          loggedInPath: null,
          logoutPath: currentPath,
        })),
      forceLogout: (currentPath: string) =>
        set((state) => ({
          isLogin: false,
          token: '',
          //logoutが呼ばれた後にuseAuthGuardでforceLogoutが呼ばれてしまうため一致する場合はnullにする
          loggedInPath: state.logoutPath === currentPath ? null : currentPath,
          logoutPath: null,
        })),
    }),
    {
      name: 'auth-store',
      storage: createJSONStorage(() => sessionStorage),
    },
  ),
);

type AuthLocationState = {
  text: string;
  provisionalToken: string;
};

// 認証関連のTokenや遷移先を管理する
export const useAuthHandler = () => {
  const getToken = useAuthStore((state) => state.getToken);
  const saveToken = useAuthStore((state) => state.saveToken);
  const logoutStore = useAuthStore((state) => state.logout);
  const forceLogoutStore = useAuthStore((state) => state.forceLogout);
  const loggedInPath = useAuthStore((state) => state.loggedInPath);
  const location = useLocation();
  const authLocationState = location.state as AuthLocationState;
  const navigate = useNavigate();

  const onLoggedInSuccess = useCallback(
    (param: { text: string; provisionalToken: string }) => {
      navigate(PagePath.SendSMS, {
        state: {
          text: param.text,
          provisionalToken: param.provisionalToken,
        },
      });
    },
    [navigate],
  );

  const onSmsCertificatedSuccess = useCallback(
    (token: string) => {
      saveToken(token);
      navigate(loggedInPath || PagePath.Home);
    },
    [loggedInPath, saveToken, navigate],
  );

  const logout = useCallback(
    async (client: Client) => {
      await client.mutation(LogoutDocument, {}, { requestPolicy: 'network-only' });
      logoutStore(window.location.pathname + window.location.search);
      window.location.href = PagePath.Login;
    },
    [logoutStore],
  );

  const redirectToLogin = useCallback(() => {
    // 現在のパスをStoreに格納しsmsCertificatedNavigateでログイン前のパスに遷移させる
    // 1.ログイン必須のページをURL指定 → 2.未ログイン時にログインページに遷移 →　ログイン後に1のページに遷移する
    forceLogoutStore(window.location.pathname + window.location.search);
    window.location.href = PagePath.Login;
  }, [forceLogoutStore]);

  return {
    authLocationState,
    onLoggedInSuccess,
    onSmsCertificatedSuccess,
    logout,
    getToken,
    redirectToLogin,
  };
};

export const useAuthGuard = () => {
  const isLogin = useAuthStore((state) => state.isLogin);
  const { redirectToLogin } = useAuthHandler();

  useEffect(() => {
    if (!isLogin) {
      redirectToLogin();
    }
  }, [isLogin, redirectToLogin]);

  return {
    isLogin,
  };
};
